import has from 'lodash/has'
import isNull from 'lodash/isNull'
import mapValues from 'lodash/mapValues'
import omit from 'lodash/omit'
import { getClientHistory } from '../appHistory'

import { serializeSearchState, deserializeSearchQuery } from './utils/search'
import { difference } from '../components/utils/objectUtils'

export const RESULTS_PAGING = 9

export const searchTypes = [
  { id: 'accommodation', slug: 'accommodation', name: 'Accommodation' },
  { id: 'adventures', slug: 'adventures', name: 'Adventures' },
  { id: 'experiences', slug: 'experiences', name: 'Experiences' },
  { id: 'motorVehicle', slug: 'motorVehicle', name: 'Vehicles' },
]

export const disallowedFiltersForType = {
  accommodation: [],
  experiences: ['petFriendly', 'amenities'],
  motorVehicle: ['petFriendly'],
  stories: [
    'priceRange',
    'priceStart',
    'priceEnd',
    'dates',
    'startDate',
    'endDate',
    'capacity',
    'petFriendly',
    'subcategories',
    'amenities',
    'roomsAndBeds',
    'instantBook',
    'fullRefund',
    'oneNightStayWeekday',
    'oneNightStayWeekend',
  ],
  adventures: [
    'adults',
    'children',
    'priceRange',
    'priceStart',
    'priceEnd',
    'dates',
    'startDate',
    'endDate',
    'capacity',
    'petFriendly',
    'subcategories',
    'amenities',
    'roomsAndBeds',
    'instantBook',
    'fullRefund',
    'oneNightStayWeekday',
    'oneNightStayWeekend',
  ],
}

export const mapFilters = (oldFilters, newFilters) => {
  let mappedFilters = newFilters

  /**
   * We need to clear certain filters according
   * to the listing type
   */
  if (newFilters.type) {
    mappedFilters = mapValues(newFilters, (value, key) =>
      disallowedFiltersForType[newFilters.type].indexOf(key) === -1
        ? value
        : null
    )
  }

  if (
    (newFilters.startDate && !newFilters.endDate) ||
    (newFilters.endDate && !newFilters.startDate)
  ) {
    mappedFilters = omit(mappedFilters, ['startDate', 'endDate'])
  }

  if (mappedFilters.startDate || mappedFilters.endDate) {
    // "One night stay" filters don't apply if searching by a date range:
    mappedFilters = omit(mappedFilters, [
      'oneNightStayWeekday',
      'oneNightStayWeekend',
    ])
  }

  if ((newFilters.ne && !newFilters.sw) || (newFilters.sw && !newFilters.ne)) {
    mappedFilters = omit(mappedFilters, ['ne', 'sw', 'origin'])
  }

  // Difference between old filters and new filters
  const diff = difference(mappedFilters, oldFilters)
  const hasChanged = (key) => has(diff, key)
  const hasChangedAndNotNull = (key) =>
    hasChanged(key) && !isNull(newFilters[key])

  /**
   * When location bounds are changed, we need to clear the regions filter
   */
  if (hasChanged('type')) {
    mappedFilters.subcategories = null
    mappedFilters.amenities = null
  }

  /**
   * When location bounds are changed, we need to clear the regions filter
   */
  if (hasChangedAndNotNull('sw') || hasChangedAndNotNull('ne')) {
    mappedFilters.state = null
    mappedFilters.regions = null
  }

  /**
   * When regions filter is changed, we need to clear the bounds filters
   */
  if (hasChanged('regions')) {
    mappedFilters.sw = null
    mappedFilters.ne = null
    mappedFilters.location = null
    mappedFilters.origin = null
  }

  /**
   * When state filter is changed, we need to clear the bounds filters
   * and the regions filter
   */
  if (hasChanged('state') && !hasChanged('regions')) {
    mappedFilters.sw = null
    mappedFilters.ne = null
    mappedFilters.regions = null
    mappedFilters.location = null
    mappedFilters.origin = null
  }

  return mappedFilters
}

function updateQueryString(filters, pathname) {
  return () => {
    const newQuery = serializeSearchState(filters)

    // reload via react-router if new pathname is not explicitly provided
    if (!pathname) {
      const history = getClientHistory()
      history.push(`${history.location.pathname}?${newQuery}`)
      return
    }

    // full page reload for new pathname
    window.location.href = `${pathname}?${newQuery}`
  }
}

export function initializeFromQueryString(queryString) {
  return (dispatch) => {
    const newState = deserializeSearchQuery(queryString)

    return dispatch({
      type: 'SEARCH_INITIALIZE_FROM_QUERY_STRING',
      payload: newState,
    })
  }
}

/**
 * Updates filters encoded in the URL search params.
 *
 * `location` filter can be included to describe a geographical area encompassed in the search. This can be queried
 *  elsewhere in the app - e.g. to display meaningful text in the mini search bar.
 *   */
export function updateFilters(filtersToUpdate, pathname) {
  return (dispatch) => {
    const currentFilters = deserializeSearchQuery(
      getClientHistory().location.search
    )

    // Clear weekendMonths when searching for specific dates range
    if (filtersToUpdate.startDate && filtersToUpdate.endDate) {
      currentFilters.weekendMonths = null
    }

    // Clear start and end date when searching for available weekends in certain months
    if (filtersToUpdate.weekendMonths) {
      currentFilters.startDate = null
      currentFilters.endDate = null
    }

    const newFilters = { ...currentFilters, ...filtersToUpdate }
    const mappedFilters = mapFilters(currentFilters, newFilters)

    dispatch(updateQueryString(mappedFilters, pathname))
  }
}

// Alias for changing a single filter
export function updateFilter(key, value) {
  return (dispatch) => {
    const newFilters = {}
    newFilters[key] = value
    dispatch(updateFilters(newFilters))
  }
}

export function resetSecondLevelFilters({ preserveStateParam = true }) {
  return (dispatch) => {
    const filters = deserializeSearchQuery(getClientHistory().location.search)

    const paramsToPreserve = [
      'type',
      'country',
      preserveStateParam && 'state',
    ].filter((param) => param != null)

    const newFilters = mapValues(filters, (value, key) =>
      paramsToPreserve.indexOf(key) > -1 ? value : null
    )
    dispatch(updateFilters(newFilters))
  }
}
